import React, { useEffect, useState } from 'react';
import {
  Stack, Typography, TextField, Box, FormControlLabel, Switch, Paper,
  IconButton, Tooltip, Button, Autocomplete,
} from '@mui/material';
import { Add, DeleteOutline, DescriptionOutlined } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard } from '../tabLayout';
import { ToneChip } from '../../common/StatusChip';
import { ListRow, QuietNote } from './sectionParts';
import { assignKeys } from '../../../utils/fieldKey';
import { AssessmentPathway, DocumentChecklistItem } from '../../../types/admissions';

interface Props {
  pathway: AssessmentPathway;
  canEdit: boolean;
  onSave: (patch: Partial<AssessmentPathway>) => Promise<void>;
}

/**
 * DocumentsSection — the per-cycle document checklist shown on the public
 * apply page + used by the Candidate drawer's Documents tab to tick off
 * what's been uploaded + verified.
 */

const COMMON_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png'];


const DocumentsSection: React.FC<Props> = ({ pathway, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DocumentChecklistItem[]>([]);

  useEffect(() => {
    if (editing) setDraft(pathway.documentChecklist || []);
  }, [editing, pathway.documentChecklist]);

  const effective = editing ? draft : (pathway.documentChecklist || []);
  const requiredCount = effective.filter(d => d.required).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ documentChecklist: assignKeys(draft.filter(d => d.label.trim()), d => d.label, 'doc', 40) });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => setDraft(d => [...d, {
    key: '',
    label: '',
    required: false,
    fileTypes: ['pdf', 'jpg', 'png'],
  }]);

  const updateItem = (idx: number, patch: Partial<DocumentChecklistItem>) =>
    setDraft(d => d.map((item, i) => (i === idx ? { ...item, ...patch } : item)));

  const removeItem = (idx: number) => setDraft(d => d.filter((_, i) => i !== idx));

  return (
    <SectionCard
      title="Document checklist"
      description={
        effective.length > 0
          ? `The only place documents are asked for — shown on the online form, the walk-in form and the parent's status page. ${effective.length} in total, ${requiredCount} required (must be verified before enrollment).`
          : 'The only place documents are asked for — shown on the online form, the walk-in form and the parent\'s status page.'
      }
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft(pathway.documentChecklist || [])}
      saving={saving}
      headerActions={
        editing && (
          <Button size="small" startIcon={<Add fontSize="small" />} onClick={addItem}>
            Add item
          </Button>
        )
      }
    >
      {effective.length === 0 ? (
        <QuietNote>
          {editing
            ? 'No documents yet. Use "Add item" to ask parents for a document.'
            : 'No documents required — parents can apply without uploading anything.'}
        </QuietNote>
      ) : !editing ? (
        <TableCard>
          {effective.map((item, idx) => (
            <ListRow key={item.key || idx}>
              <DescriptionOutlined fontSize="small" sx={{ color: 'text.disabled' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                {(item.fileTypes || []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {(item.fileTypes || []).map(ft => ft.toUpperCase()).join(', ')}
                  </Typography>
                )}
              </Box>
              <ToneChip label={item.required ? 'Required' : 'Optional'} tone={item.required ? 'highlight' : 'neutral'} />
            </ListRow>
          ))}
        </TableCard>
      ) : (
        <Stack spacing={1.5}>
          {effective.map((item, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Label"
                    fullWidth
                    value={item.label}
                    onChange={(e) => updateItem(idx, { label: e.target.value })}
                    placeholder="e.g. Birth certificate"
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removeItem(idx)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={item.required}
                        onChange={(_, v) => updateItem(idx, { required: v })}
                      />
                    }
                    label="Required"
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={COMMON_FILE_TYPES}
                    value={item.fileTypes || []}
                    onChange={(_, next) =>
                      updateItem(idx, { fileTypes: (next as string[]).map(s => s.toLowerCase()) })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Accepted file types" placeholder="pdf, jpg…" />
                    )}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

export default DocumentsSection;
